import { ApiProvider } from '@/provider/ApiProvider';

import type { Result } from '../api';
import type { ScopeRule } from '../types';
import { tenantRefusal } from './refusals';
import { publicIdentityRequest } from './publicRequest';
import type {
  AcceptAnswer,
  ApproveInput,
  ChangeInput,
  AccessHistoryItem,
  AccessHistoryQuery,
  InvitableRole,
  InvitationCheck,
  InvitationInput,
  InvitationLookup,
  MembershipChangeInput,
  MembershipRecord,
  MyInvitation,
  OrganisationSetup,
  Paged,
  SetupChange,
  TenantPackage,
} from './types';

/**
 * Calls of the tenant administration (tenant-admin-api.md) on the identity
 * service: `/admin/tenant/...` and `/me/invitations...` through
 * ApiProvider.identityRequest (token + X-PNET-Expected-Assignment like every
 * identity call), the public `/v1/...` routes without a token.
 */

const unwrap = <T>(payload: unknown): Result<T> => {
  const refusal = tenantRefusal(payload);
  if (refusal) return { ok: false, refusal };
  const body = payload as { data?: T; message?: unknown };
  return { ok: true, data: body.data as T, message: String(body.message ?? '') };
};

const paged = <T>(r: Result<unknown>, page: number, perPage: number): Result<Paged<T>> => {
  if (!r.ok) return r;
  const d = (r.data ?? {}) as Partial<Paged<T>>;
  const items = Array.isArray(d.items) ? d.items : Array.isArray(r.data) ? (r.data as T[]) : [];
  return {
    ok: true,
    message: r.message,
    data: {
      items,
      total: Number(d.total ?? items.length),
      page: Number(d.page ?? page),
      per_page: Number(d.per_page ?? perPage),
    },
  };
};

const query = (q: Record<string, string | number | null | undefined>): string => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q))
    if (v !== null && v !== undefined && String(v).trim() !== '') params.set(k, String(v).trim());
  const s = params.toString();
  return s ? `?${s}` : '';
};

const get = async <T>(path: string): Promise<Result<T>> =>
  unwrap<T>(await ApiProvider.identityRequest('GET', path));

const post = async <T>(path: string, body: Record<string, unknown> = {}): Promise<Result<T>> =>
  unwrap<T>(await ApiProvider.identityRequest('POST', path, body));

// ---- availability ---------------------------------------------------------------------------

/** GET admin/tenant/capabilities: what the active assignment may use (always 200 when set up). */
export interface TenantCapabilities {
  setup: boolean;
  invite: boolean;
  history: boolean;
  approve: boolean;
  /** the Rules (security rules) page: Super Admins only (admin-levels-2026-10-15.md) */
  rules: boolean;
  /** memberships (GET admin/tenant/memberships): ASI staff, Super Admins, organisation admins */
  memberships: boolean;
  /** "My requests" (GET admin/tenant/approvals?mine=1): internal admins who do not approve */
  my_requests: boolean;
}

/**
 * What the tenant administration offers the caller's ACTIVE assignment:
 * set-up, invite, history, approve. Answers 200 for every signed-in user
 * (so no refusal on every settings page); 503
 * tenant_administration_unavailable while its database update has not run.
 */
export async function getTenantCapabilities(): Promise<Result<TenantCapabilities>> {
  const r = await get<Partial<TenantCapabilities> | null>('admin/tenant/capabilities');
  if (!r.ok) return r;
  const d = r.data ?? {};
  return {
    ...r,
    data: {
      setup: d.setup === true,
      invite: d.invite === true,
      history: d.history === true,
      approve: d.approve === true,
      rules: d.rules === true,
      memberships: d.memberships === true,
      my_requests: d.my_requests === true,
    },
  };
}

// ---- packages and organisation set-up --------------------------------------------------------

export async function listPackages(type: string): Promise<Result<TenantPackage[]>> {
  const r = await get<{ items?: TenantPackage[] }>(`admin/tenant/packages${query({ type })}`);
  if (!r.ok) return r;
  return { ...r, data: Array.isArray(r.data?.items) ? r.data.items : [] };
}

export async function getOrganisationSetup(contextId: number): Promise<Result<OrganisationSetup>> {
  return get(`admin/tenant/organisations/${contextId}`);
}

export async function listInvitableRoles(contextId: number): Promise<Result<InvitableRole[]>> {
  const r = await get<{ items?: InvitableRole[] }>(
    `admin/tenant/organisations/${contextId}/invitable-roles`,
  );
  if (!r.ok) return r;
  return { ...r, data: Array.isArray(r.data?.items) ? r.data.items : [] };
}

export async function prepareChange(
  contextId: number,
  body: ChangeInput,
): Promise<Result<SetupChange>> {
  return post(`admin/tenant/organisations/${contextId}/changes`, { ...body });
}

export interface ChangeQuery {
  contextId?: number | null;
  state?: string | null;
  page?: number;
  perPage?: number;
}

export async function listChanges(q: ChangeQuery = {}): Promise<Result<Paged<SetupChange>>> {
  const page = q.page ?? 1;
  const perPage = q.perPage ?? 25;
  return paged<SetupChange>(
    await get(
      `admin/tenant/changes${query({ context_id: q.contextId, state: q.state, page, per_page: perPage })}`,
    ),
    page,
    perPage,
  );
}

export type ChangeDecision = 'approve' | 'reject' | 'cancel';

export async function decideChange(
  id: number,
  decision: ChangeDecision,
  text?: string,
): Promise<Result<SetupChange>> {
  const body: Record<string, unknown> = {};
  if (decision === 'approve' && text) body.note = text;
  if (decision === 'reject') body.reason = text ?? '';
  return post(`admin/tenant/changes/${id}/${decision}`, body);
}

// ---- invitations, approvals, history ---------------------------------------------------------

const invitationBody = (input: InvitationInput): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    email: input.email.trim(),
    role_id: input.role_id,
    rules: input.rules,
    valid_until: input.valid_until,
  };
  if (input.context_id) body.context_id = input.context_id;
  if (input.first_name?.trim()) body.first_name = input.first_name.trim();
  if (input.last_name?.trim()) body.last_name = input.last_name.trim();
  if (input.reason?.trim()) body.reason = input.reason.trim();
  if (input.currency_id) body.currency_id = input.currency_id;
  return body;
};

export async function checkInvitation(input: InvitationInput): Promise<Result<InvitationCheck>> {
  return post('admin/tenant/invitations/check', invitationBody(input));
}

export async function createInvitation(input: InvitationInput): Promise<Result<MembershipRecord>> {
  return post('admin/tenant/invitations', invitationBody(input));
}

/**
 * Paths of the reads the pages ask through useAccessQuery (the answer body is read there:
 * shared loading / no-access / error states). Parameters in a fixed order.
 */
export const approvalsPath = (
  q: {
    contextId?: number | null;
    mine?: boolean;
    status?: string | null;
    page?: number;
    perPage?: number;
  } = {},
): string =>
  `admin/tenant/approvals${query({
    context_id: q.contextId,
    mine: q.mine ? 1 : null,
    status: q.status,
    page: q.page ?? 1,
    per_page: q.perPage ?? 25,
  })}`;

/** The Invited tab: GET admin/tenant/invitations (`sent_by=me`: only the caller's). */
export const invitationsPath = (
  q: {
    contextId?: number | null;
    sentByMe?: boolean;
    search?: string | null;
    page?: number;
    perPage?: number;
  } = {},
): string =>
  `admin/tenant/invitations${query({
    context_id: q.contextId,
    sent_by: q.sentByMe ? 'me' : null,
    search: q.search,
    page: q.page ?? 1,
    per_page: q.perPage ?? 25,
  })}`;

/** One person's memberships (a person's page): internal callers every organisation's. */
export const personMembershipsPath = (personId: number): string =>
  `admin/tenant/memberships${query({ person_id: personId, per_page: 100 })}`;

/** The raw answer of an identity GET, for useAccessQuery. */
export const identityGet = (path: string): Promise<unknown> =>
  ApiProvider.identityRequest('GET', path);

/** `{items, total, page, per_page}` of a list answer's `data` (empty when it carries none). */
export function pageOf<T>(data: unknown): Paged<T> {
  const d = (data && typeof data === 'object' ? data : {}) as Partial<Paged<T>>;
  const items = Array.isArray(d.items) ? d.items : Array.isArray(data) ? (data as T[]) : [];
  return {
    items,
    total: Number(d.total ?? items.length),
    page: Number(d.page ?? 1),
    per_page: Number(d.per_page ?? items.length),
  };
}

export async function approveRequest(
  id: number,
  input: ApproveInput = {},
): Promise<Result<MembershipRecord>> {
  const body: Record<string, unknown> = {};
  if (input.context_id !== undefined) body.context_id = input.context_id;
  if (input.role_id !== undefined) body.role_id = input.role_id;
  if (input.rules !== undefined) body.rules = input.rules as ScopeRule[] | null;
  if (input.valid_until !== undefined) body.valid_until = input.valid_until;
  if (input.note?.trim()) body.note = input.note.trim();
  return post(`admin/tenant/requests/${id}/approve`, body);
}

export async function rejectRequest(id: number, reason: string): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/requests/${id}/reject`, { reason: reason.trim() });
}

// ---- memberships ---------------------------------------------------------------------------

/**
 * PATCH admin/tenant/memberships/{id}: role, access, end date (a person's page, owner
 * 2026-10-16). A new role ends the old row and answers the NEW membership (another `id`);
 * making or removing an administrator answers 202 with a request record.
 */
export async function changeMembership(
  id: number,
  input: MembershipChangeInput,
): Promise<Result<MembershipRecord>> {
  const body: Record<string, unknown> = {};
  if (input.role_id !== undefined) body.role_id = input.role_id;
  if (input.rules !== undefined) body.rules = input.rules;
  if (input.valid_until !== undefined) body.valid_until = input.valid_until;
  if (input.reason?.trim()) body.reason = input.reason.trim();
  return unwrap(await ApiProvider.identityRequest('PATCH', `admin/tenant/memberships/${id}`, body));
}

/** POST admin/tenant/memberships/{id}/end (an administrator: 202, a request for a second person). */
export async function endMembership(id: number, reason: string): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/memberships/${id}/end`, { reason: reason.trim() });
}

/** POST admin/tenant/memberships/{id}/renew: a new end date (at most 12 months away). */
export async function renewMembership(
  id: number,
  validUntil: string,
): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/memberships/${id}/renew`, { valid_until: validUntil });
}

/** POST admin/tenant/memberships/{id}/confirm: keeps an external membership whose review is open. */
export async function confirmMembership(id: number): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/memberships/${id}/confirm`);
}

/** POST admin/tenant/requests/{id}/resend: a new single-use link, valid 7 days; the old one stops. */
export async function resendInvitation(id: number): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/requests/${id}/resend`);
}

/** POST admin/tenant/requests/{id}/cancel: withdraws an open request or invitation. */
export async function cancelRequest(id: number): Promise<Result<MembershipRecord>> {
  return post(`admin/tenant/requests/${id}/cancel`);
}

/**
 * GET admin/access-history: every access event (the app's own records, the
 * tenant history and database changes), newest first. Super Admins and
 * Admins see all; organisation admins their own organisation's events.
 */
export async function listAccessHistory(
  q: AccessHistoryQuery = {},
): Promise<Result<Paged<AccessHistoryItem>>> {
  const page = q.page ?? 1;
  const perPage = q.perPage ?? 25;
  const r = await get<Record<string, unknown>>(
    `admin/access-history${query({
      context_id: q.contextId,
      target_user_id: q.targetUserId,
      actor_id: q.actorId,
      from: q.from,
      to: q.to,
      source: q.source,
      approval: q.approval,
      person_id: q.personId,
      kind: q.kinds && q.kinds.length > 0 ? q.kinds.join(',') : null,
      page,
      per_page: perPage,
    })}`,
  );
  if (!r.ok) return r;
  const d = (r.data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(d.items) ? (d.items as AccessHistoryItem[]) : [];
  // access-levels.md section 7: `pagination: {page, per_page, total}`
  const p = (d.pagination && typeof d.pagination === 'object' ? d.pagination : {}) as Record<
    string,
    unknown
  >;
  return {
    ok: true,
    message: r.message,
    data: {
      items,
      total: Number(p.total ?? items.length),
      page: Number(p.page ?? page),
      per_page: Number(p.per_page ?? perPage),
    },
  };
}

// ---- the invited person ---------------------------------------------------------------------

export async function lookupInvitation(token: string): Promise<Result<InvitationLookup>> {
  return unwrap(await publicIdentityRequest('v1/invitations/lookup', { token }));
}

export async function setUpAccount(
  token: string,
  names: { first_name?: string; last_name?: string },
): Promise<Result<{ next: 'check_mail' | 'sign_in' }>> {
  const body: Record<string, unknown> = { token };
  if (names.first_name?.trim()) body.first_name = names.first_name.trim();
  if (names.last_name?.trim()) body.last_name = names.last_name.trim();
  return unwrap(await publicIdentityRequest('v1/invitations/set-up-account', body));
}

export async function acceptInvitation(token: string): Promise<Result<AcceptAnswer>> {
  return post('me/invitations/accept', { token });
}

export async function declineInvitation(token: string): Promise<Result<unknown>> {
  return post('me/invitations/decline', { token });
}

/** GET me/invitations: the signed-in person's pending invitations (no role needed). */
export async function listMyInvitations(): Promise<Result<MyInvitation[]>> {
  const r = await get<{ items?: MyInvitation[] }>('me/invitations');
  if (!r.ok) return r;
  return { ...r, data: Array.isArray(r.data?.items) ? r.data.items : [] };
}

export async function acceptMyInvitation(id: number): Promise<Result<AcceptAnswer>> {
  return post(`me/invitations/${id}/accept`);
}

export async function declineMyInvitation(id: number): Promise<Result<unknown>> {
  return post(`me/invitations/${id}/decline`);
}

// ---- Request access (F5) --------------------------------------------------------------------

export async function requestAccess(email: string): Promise<Result<null>> {
  return unwrap(await publicIdentityRequest('v1/access-requests', { email: email.trim() }));
}

export async function confirmAccessRequest(
  token: string,
  body: { first_name: string; last_name: string; message?: string },
): Promise<Result<null>> {
  const payload: Record<string, unknown> = {
    token,
    first_name: body.first_name.trim(),
    last_name: body.last_name.trim(),
  };
  if (body.message?.trim()) payload.message = body.message.trim();
  return unwrap(await publicIdentityRequest('v1/access-requests/confirm', payload));
}
